import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import useStructuredData from "../hooks/useStructuredData";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";
import { FAQS, type FaqCategory } from "../data/faq";

const CATEGORY_LABELS: Record<FaqCategory, string> = {
  supplier: "Supplier",
  franchise: "Franchise",
  b2b: "B2B / Corporate",
  general: "General",
};

const CATEGORY_ORDER: FaqCategory[] = ["supplier", "franchise", "b2b", "general"];

export default function PartnershipFaqAccordion() {
  const [openIndex, setOpenIndex] = useState<string | null>(null);
  const reducedMotion = usePrefersReducedMotion();

  useStructuredData({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  });

  return (
    <div>
      {CATEGORY_ORDER.map((category) => {
        const entries = FAQS.filter((f) => f.category === category);
        if (entries.length === 0) return null;

        return (
          <div key={category} className="mt-8 first:mt-0">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-teal-600">
              {CATEGORY_LABELS[category]}
            </h3>
            <div className="mt-3 space-y-3">
              {entries.map((faq) => {
                const id = `${category}-${faq.q}`;
                const isOpen = openIndex === id;
                return (
                  <div key={id} className="overflow-hidden rounded-2xl bg-white ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
                    <button
                      type="button"
                      onClick={() => setOpenIndex(isOpen ? null : id)}
                      aria-expanded={isOpen}
                      className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                    >
                      <span className="font-display font-semibold text-ocean-950 dark:text-foam-50">{faq.q}</span>
                      <motion.span
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: reducedMotion ? 0 : 0.2 }}
                      >
                        <ChevronDown className="h-5 w-5 shrink-0 text-ocean-800/50 dark:text-foam-100/50" />
                      </motion.span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={reducedMotion ? false : { height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: reducedMotion ? 0 : 0.25, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <p className="px-6 pb-5 text-sm leading-relaxed text-ocean-800/70 dark:text-foam-100/70">{faq.a}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <p className="mt-10 text-center text-sm text-ocean-800/60 dark:text-foam-100/60">
        Still have questions?{" "}
        <Link to="/contact" className="font-semibold text-teal-600 hover:text-teal-700">
          Contact us
        </Link>
        .
      </p>
    </div>
  );
}
